import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Typography, Box, List, ListItem, ListItemText, Divider, Paper, CircularProgress, Rating, Button } from '@mui/material';
import { getCourseDetails } from '../services/api';

interface Lesson {
  id: number;
  title: string;
  duration: string;
}

interface Author {
  name: string;
  title: string;
  experience: string;
  description: string;
}

interface Review {
  id: number;
  author: string;
  rating: number;
  text: string;
}

interface Course {
  id: number;
  title: string;
  description: string;
  forWhom: string[];
  results: string[];
  lessons: Lesson[];
  author: Author;
  reviews: Review[];
  isPurchased: boolean;
}

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const Error = styled.div`
  color: #dc3545;
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const Section = styled(Paper)`
  padding: 2rem;
  margin-bottom: 2rem;
`;

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await getCourseDetails(id!);
        setCourse(data);
      } catch (err) {
        setError('Произошла ошибка при загрузке курса');
        console.error('Error fetching course:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCourse();
    }
  }, [id]);

  if (loading) {
    return (
      <Container>
        <Loading>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Загрузка курса...
          </Typography>
        </Loading>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Error>{error}</Error>
      </Container>
    );
  }

  if (!course) {
    return (
      <Container>
        <Error>Курс не найден</Error>
      </Container>
    );
  }

  return (
    <Container>
      <Section elevation={0}>
        <Typography variant="h3" gutterBottom>
          {course.title}
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          {course.description}
        </Typography>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom>
          Для кого этот курс
        </Typography>
        <List>
          {course.forWhom.map((item, index) => (
            <ListItem key={index}>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom>
          Что вы получите
        </Typography>
        <List>
          {course.results.map((result, index) => (
            <ListItem key={index}>
              <ListItemText primary={result} />
            </ListItem>
          ))}
        </List>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom>
          Уроки курса
        </Typography>
        <List>
          {course.lessons.map((lesson) => (
            <ListItem key={lesson.id}>
              <ListItemText 
                primary={lesson.title}
                secondary={`Длительность: ${lesson.duration}`}
              />
            </ListItem>
          ))}
        </List>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom>
          Об авторе
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">{course.author.name}</Typography>
          <Typography color="text.secondary">{course.author.title}</Typography>
          <Typography color="text.secondary">{course.author.experience}</Typography>
        </Box>
        <Typography paragraph>{course.author.description}</Typography>
      </Section>

      <Section elevation={0}>
        <Typography variant="h5" gutterBottom>
          Отзывы
        </Typography>
        {course.reviews.map((review) => (
          <Box key={review.id} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                {review.author}
              </Typography>
              <Rating value={review.rating} readOnly size="small" />
            </Box>
            <Typography>{review.text}</Typography>
            <Divider sx={{ my: 2 }} />
          </Box>
        ))}
      </Section>

      {!course.isPurchased && (
        <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={() => {
              // TODO: Implement purchase logic
              console.log('Purchase course:', course.id);
            }}
          >
            Купить курс
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default CourseDetail; 